import type { shared_FlexibleDate } from '@/api';
import { parseISO, isValid, format } from "date-fns";
import { fromZonedTime, formatInTimeZone } from "date-fns-tz";
// format.ts imports the org zone from here; both sides only call each other at runtime.
import { formatDateDisplay, formatDateTime } from "@/lib/format";

/**
 * The zone every org falls back to when none has been resolved yet.
 *
 * It mirrors `dateutil.DefaultOrgTimezone` on the backend and is a documented
 * default, not a placeholder: every tenant today operates in Switzerland, so
 * falling back here keeps rendered times identical to what they were when this
 * was the only zone the app knew. Falling back to UTC instead would shift every
 * displayed time by an hour with nothing on screen to say so.
 */
export const DEFAULT_ORG_TIMEZONE = "Europe/Zurich";

let currentOrgTimezone = DEFAULT_ORG_TIMEZONE;

/**
 * The zone the org's dates and times are rendered in.
 *
 * Read through a function rather than exported as a value because it is resolved
 * asynchronously: the org record arrives after the first render, and a captured
 * constant would freeze the wrong zone into every module that imported it.
 */
export const getOrgTimezone = (): string => currentOrgTimezone;

/**
 * Install the organisation's configured timezone.
 *
 * Called once, from `useOrgTimezone`, when the org record loads. An unknown or
 * missing name is refused and the default stands — the same fail-closed rule the
 * backend applies, and for the same reason: a zone the runtime cannot resolve
 * would silently render every time in UTC.
 *
 * @returns the zone in effect after the call
 */
export const setOrgTimezone = (timezone: string | undefined | null): string => {
  if (timezone && isValidTimezone(timezone)) {
    currentOrgTimezone = timezone;
  } else if (timezone) {
    console.warn(
      `Ignoring unknown organisation timezone "${timezone}"; using ${DEFAULT_ORG_TIMEZONE}`
    );
  }
  return currentOrgTimezone;
};

/** Reset to the documented default. Exists for tests and for sign-out. */
export const resetOrgTimezone = (): void => {
  currentOrgTimezone = DEFAULT_ORG_TIMEZONE;
};

/**
 * Whether the runtime can resolve an IANA zone name.
 *
 * `Intl.DateTimeFormat` throws a RangeError on an unknown timeZone, which is the
 * only reliable check available in a browser.
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format();
    return true;
  } catch {
    return false;
  }
};

/**
 * Every IANA zone name the runtime knows, for the settings picker.
 *
 * `Intl.supportedValuesOf` is the browser's own copy of the IANA database, so
 * the picker cannot drift from what the runtime can actually resolve. Older
 * runtimes (and some test environments) lack it; there the list falls back to
 * the default zone alone, which keeps the field usable rather than empty.
 */
export const supportedTimezones = (): string[] => {
  const supportedValuesOf = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supportedValuesOf !== "function") {
    return [DEFAULT_ORG_TIMEZONE];
  }
  try {
    const zones = supportedValuesOf("timeZone");
    return zones.length > 0 ? zones : [DEFAULT_ORG_TIMEZONE];
  } catch {
    return [DEFAULT_ORG_TIMEZONE];
  }
};

/**
 * Convert a wall-clock time in the org timezone into a true instant (ISO string).
 *
 * Produces the same instant the mobile app's `toISOString()` yields for the same
 * wall-clock moment, and is offset-aware (CET in winter, CEST in summer).
 *
 * @param date - calendar date as "yyyy-MM-dd"
 * @param time - wall-clock time as "HH:mm" or "HH:mm:ss"
 * @returns the instant as an ISO string, or undefined when input is missing/invalid
 */
export const orgWallClockToISO = (
  date: string | undefined | null,
  time: string | undefined | null
): string | undefined => {
  if (!date || !time) return undefined;

  const match = /^(\d{2}:\d{2})(:\d{2})?$/.exec(time);
  if (!match) return undefined;
  // Normalize "HH:mm" to "HH:mm:ss"
  const normalizedTime = match[2] ? time : `${time}:00`;

  const instant = fromZonedTime(`${date}T${normalizedTime}`, getOrgTimezone());
  if (!isValid(instant)) return undefined;
  return instant.toISOString();
};

/**
 * Render an instant as "HH:mm" wall-clock time in the org timezone.
 *
 * Independent of the browser's timezone. Returns "" for falsy input — the "—"
 * empty placeholder is a presentation concern for the caller, not this helper.
 *
 * @returns "HH:mm", "" for falsy input, or the input unchanged when unparseable
 */
export const formatOrgTime = (isoStr: string | undefined | null): string => {
  if (!isoStr) return "";
  const parsed = parseISO(isoStr);
  if (!isValid(parsed)) return isoStr;
  return formatInTimeZone(parsed, getOrgTimezone(), "HH:mm");
};

/**
 * Reduce a date value to its calendar date ("yyyy-MM-dd") in the org timezone.
 *
 * A bare "yyyy-MM-dd" passes through untouched — never round-trip it through
 * `new Date()`, which parses it as UTC midnight and shifts the day on any
 * browser at a negative UTC offset.
 *
 * @returns "yyyy-MM-dd", "" for falsy input, or the input unchanged when unparseable
 */
export const toOrgDateOnly = (val: string | undefined | null): string => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const parsed = parseISO(val);
  if (!isValid(parsed)) return val;
  return formatInTimeZone(parsed, getOrgTimezone(), "yyyy-MM-dd");
};

/**
 * Format a `Date` as its LOCAL calendar day, "yyyy-MM-dd".
 *
 * This is the helper to reach for when serializing a date-only value picked in
 * `ui/date-picker.tsx` (which builds `Date`s at local midnight). Never use
 * `toISOString()` for this: it converts to UTC, so a local-midnight `Date` in a
 * zone AHEAD of UTC serializes to the PREVIOUS calendar day
 * (2026-05-01 00:00 Zurich -> "2026-04-30T22:00:00.000Z").
 *
 * Reads the browser's local calendar, which is the right zone whenever the value
 * came from a local-midnight `Date` the user just picked. Siblings:
 * - `toOrgDateOnly(str)` — same output, but from a STRING, in the ORG zone.
 * - `toUtcMidnightTimestamp(date)` — full "…T00:00:00Z" RFC3339 for endpoints
 *   whose Go DTO binds `time.Time` and rejects a bare date with a 400.
 *
 * @returns "yyyy-MM-dd", or "" for missing/invalid input
 */
export const formatLocalDate = (date: Date | undefined | null): string => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

export const parseDateString = (
  dateString: string | undefined | null
): Date | undefined => {
  if (!dateString) return undefined;
  try {
    // Use parseISO for consistent timezone handling
    const parsed = parseISO(dateString);
    if (isValid(parsed)) {
      return parsed;
    }
    // Invalid format: log diagnostic (not user-visible) and return undefined.
    // Callers treat undefined as "no date" and decide how to surface that.
    console.warn(`Failed to parse date string (invalid format): ${dateString}`);
    return undefined;
  } catch (error) {
    console.warn(`Failed to parse date string: ${dateString}`, error);
    return undefined;
  }
};

/**
 * Numeric date, `dd.MM.yyyy` in every locale.
 *
 * @deprecated Use `formatDateDisplay` from `@/lib/format`; this name stays for its callers.
 */
export const formatDate = (
  date: Date | string | undefined | null,
  locale?: string
): string => formatDateDisplay(date, locale);

/**
 * Numeric date and 24-hour time (`dd.MM.yyyy HH:mm`) in the org timezone.
 *
 * @deprecated Use `formatDateTime` from `@/lib/format`; this name stays for its callers.
 */
export const formatTimestamp = (
  date: Date | string | undefined | null,
  locale?: string
): string => formatDateTime(date, locale);

/**
 * Format a date as an RFC3339 timestamp at UTC midnight ("yyyy-MM-ddT00:00:00Z").
 *
 * For endpoints whose Go DTO binds the field as `time.Time` — echo's binder
 * rejects a bare "yyyy-MM-dd" with a 400 before the handler runs. The calendar
 * day is taken from the LOCAL date parts, then stamped as UTC midnight.
 *
 * If you want a bare "yyyy-MM-dd" (most endpoints), use `formatLocalDate`.
 * Some endpoints — e.g. GET /allocations — reject the "…T00:00:00Z" form.
 */
export const toUtcMidnightTimestamp = (date: Date | string): string => {
  if (typeof date === "string") {
    const trimmed = date.trim();
    // If it's a bare date (YYYY-MM-DD), normalize to UTC midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return `${trimmed}T00:00:00Z`;
    }
    // If it's a full ISO string, coerce to the same calendar date at UTC midnight
    const parsed = parseISO(trimmed);
    if (isValid(parsed)) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T00:00:00Z`;
    }
    // Unknown format: return as-is rather than corrupting the value
    return trimmed;
  }
  
  // Create a new date at midnight UTC for the given date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}T00:00:00Z`;
};

/**
 * Reads a `shared_FlexibleDate` field as the plain date string it actually carries.
 *
 * The Go type marshals as a string (`FlexibleDate` is tagged `swaggertype:"string"`,
 * internal/handlers/shared/common_types.go:120), so at runtime these fields ARE
 * strings — but swag still generates them as the wrapper object `{ time?: string }`.
 * This accepts either representation and needs no cast: the `typeof` test narrows the
 * object arm away on the string branch.
 */
export const flexibleDateToString = (
  value: shared_FlexibleDate | string | undefined | null,
): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return typeof value === 'string' ? value : value.time;
};
